import ClearIcon from "@mui/icons-material/Clear"
import DoneIcon from "@mui/icons-material/Done"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material"
import { useAppSelector } from "hooks"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { CompleteStatus, type ITrainingReport } from "types/trainingReportTypes"

type Props = {
  trainingReports: Array<ITrainingReport>
}

export function ExamList({ trainingReports }: Props) {
  const navigate = useNavigate()

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)

  const lessons = useAppSelector((state) => state.lesson.lessons)

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const getLessonName = (lessonId: string): string => {
    var lesson = lessons.find((x) => x.id === lessonId)

    if (lesson === undefined) {
      return "Undefined Lesson"
    }

    return lesson.name
  }

  const getCraneParameter = (trainingReport: ITrainingReport): string => {
    if (trainingReport.parameters === undefined) {
      return "-"
    }

    const craneParameter = trainingReport.parameters.find((x) => x.name === "crane")

    if (craneParameter) {
      return craneParameter.value
    }

    return "-"
  }

  const getToolParameter = (trainingReport: ITrainingReport): string => {
    if (trainingReport.parameters === undefined) {
      return "-"
    }

    const toolParameter = trainingReport.parameters.find((x) => x.name === "tool")

    if (toolParameter) {
      return toolParameter.value
    }

    return "-"
  }

  const getExamResultParameter = (trainingReport: ITrainingReport): string => {
    if (trainingReport.completeStatus === CompleteStatus.Quit) {
      return "Quit"
    }
    if (trainingReport.parameters === undefined) {
      return "-"
    }

    const examResultParameter = trainingReport.parameters.find((x) => x.name === "exam_result")

    if (examResultParameter) {
      return examResultParameter.value
    }

    return "-"
  }

  const getMinutesBetweenDates = (startDate: Date, endDate: Date): number => {
    const msBetween = endDate.getTime() - startDate.getTime()
    const seconds = Math.floor(msBetween / 1000)
    const minutes = Math.floor(seconds / 60)

    return minutes
  }

  return (
    <TableContainer>
      <Table size="medium">
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>Exam</TableCell>
            <TableCell>Result</TableCell>
            <TableCell>Crane</TableCell>
            <TableCell>Accessory</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {trainingReports
            .slice()
            .sort((x, y) => {
              var dateA = new Date(y.startDate).getTime()
              var dateB = new Date(x.startDate).getTime()
              return dateA > dateB ? 1 : -1
            })
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((trainingReport) => (
              <TableRow
                key={trainingReport.id}
                hover
                onClick={() => navigate(`exam/${trainingReport.id}`)}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell>
                  {getExamResultParameter(trainingReport) === "Pass" ? (
                    <DoneIcon color="success" />
                  ) : (
                    <ClearIcon color="error" />
                  )}
                </TableCell>
                <TableCell>{getLessonName(trainingReport.lessonId)}</TableCell>
                <TableCell>{getExamResultParameter(trainingReport)}</TableCell>
                <TableCell>{getCraneParameter(trainingReport)}</TableCell>
                <TableCell>{getToolParameter(trainingReport)}</TableCell>
                <TableCell>
                  {`${getMinutesBetweenDates(
                    new Date(trainingReport.startDate),
                    new Date(trainingReport.endDate),
                  )} min`}
                </TableCell>
                <TableCell>{new Date(trainingReport.endDate).toLocaleString()}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        rowsPerPageOptions={[5, 10, 25]}
        count={trainingReports.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  )
}
